import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Loader2, ShieldAlert } from 'lucide-react';

// Loading spinner component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#0F172A]">
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
    </motion.div>
  </div>
);

// Access denied component
const AccessDenied = ({ message }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#0F172A]">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center max-w-md px-6"
    >
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
        <ShieldAlert className="w-10 h-10 text-red-500" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Access Denied</h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
      <a
        href="/app"
        className="inline-flex items-center px-6 py-3 rounded-xl bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] text-white font-medium hover:opacity-90 transition-opacity"
      >
        Go to Dashboard
      </a>
    </motion.div>
  </div>
);

/**
 * ProtectedRoute component
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if authorized
 * @param {boolean} props.requireAdmin - Whether admin role is required
 * @param {string} props.pageId - The page ID for permission checking
 */
const ProtectedRoute = ({ children, requireAdmin = false, pageId = null }) => {
  const { isAuthenticated, loading, isAdmin, hasPageAccess } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth status
  if (loading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return <AccessDenied message="This page requires administrator privileges." />;
  }

  // Check page-specific permission
  if (pageId && !hasPageAccess(pageId)) {
    return <AccessDenied message="You don't have permission to access this page." />;
  }

  return children;
};

export default ProtectedRoute;
